import { OnCatchError } from '@/clients/shared-components/ErrorBoundaries';
import { ErrorScreen } from '@/clients/shared-components/ErrorScreen';
import {
  LocalizationContextProvider,
  useLocalizationContext,
} from '@/clients/shared-hooks/use-localization-context';
import { useRudderstack } from '@/clients/shared-hooks/use-rudderstack';
import { SignedInRouter } from '@/clients/signed-in/router';
import { SignedOutRouter } from '@/clients/signed-out/router';
import { clearClerkAuthToken, setClerkAuthToken } from '@/clients/util/clerk';
import { ENV_VARS } from '@/clients/util/env';
import { applyHtmlLangDir, initializeI18n, loadAndActivateI18n } from '@/clients/util/i18n';
import { ClerkProvider, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssVarsDesignTokenProvider } from 'css-vars-design-token';
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { themes } from './clients/shared-components/themes';
import './css/index.css';

/** Global query client for all queries in the application */
const queryClient = new QueryClient();

/** Initialize the i18n with the default locale */
initializeI18n();

/**
 * The main application component that initializes Clerk and sets up the routing.
 * It handles uncaught errors globally and provides a localization context for Clerk
 * and sets up any needed global providers
 */

export function Application() {
  useRudderstack();
  const [hasUncaughtError, setHasUncaughtError] = useState(false);
  return (
    <OnCatchError handleError={() => setHasUncaughtError(true)}>
      {hasUncaughtError ? (
        <ErrorScreen message="Unexpected error" />
      ) : (
        <LocalizationContextProvider>
          <I18nProvider i18n={i18n}>
            <LocalizedProviders />
          </I18nProvider>
        </LocalizationContextProvider>
      )}
    </OnCatchError>
  );
}

function LocalizedProviders() {
  const localization = useLocalizationContext();
  useEffect(() => {
    loadAndActivateI18n(localization.code);
    applyHtmlLangDir(localization.code);
  }, [localization.code]);
  return (
    <ClerkProvider
      publishableKey={ENV_VARS.VITE_CLERK_PUBLISHABLE_KEY}
      localization={localization.clerk}
    >
      <QueryClientProvider client={queryClient}>
        <CssVarsDesignTokenProvider themes={themes} theme="light">
          <BrowserRouter>
            <RoutesLoader />
          </BrowserRouter>
        </CssVarsDesignTokenProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

/**
 * RouterLoader component that manages authentication state and token updates.
 * It renders different routers based on the user's signed-in status.
 */
function RoutesLoader() {
  useTokenManagement();
  return (
    <>
      <SignedOut>
        <SignedOutRouter />
      </SignedOut>
      <SignedIn>
        <SignedInRouter />
      </SignedIn>
    </>
  );
}

/**
 * Ensures that the Clerk auth token is updated when the user signs in or out.
 */
function useTokenManagement() {
  const auth = useAuth();
  const [token, setToken] = useState('');
  const updateToken = useCallback(() => {
    auth.getToken().then((token) => {
      if (!token) {
        clearClerkAuthToken();
      } else {
        setToken(token);
        setClerkAuthToken(token);
      }
    });
  }, [auth]);
  useEffect(() => {
    if (auth.isLoaded && auth.isSignedIn && !token) updateToken();
  }, [auth, token, updateToken]);
  useEffect(() => {
    if (auth.isLoaded && !auth.isSignedIn) clearClerkAuthToken();
  }, [auth, token]);
}
